<script lang="ts">
  import Icon from './Icon.svelte';
  import { qrUrl } from '../lib/api/client';
  import { t } from '../i18n/index.svelte';

  let {
    shareUrl,
    token,
    authKey = null,
    onCopy,
    onClose,
  }: {
    shareUrl: string;
    token: string;
    /** Author xem model private → QR cần kèm ?edit= để GET được ảnh (backend gate endpoint này). */
    authKey?: string | null;
    onCopy: (msg: string) => void;
    onClose: () => void;
  } = $props();

  let qrFailed = $state(false);

  const embedUrl = $derived(`${shareUrl}?embed=1`);
  const iframeCode = $derived(
    `<iframe src="${embedUrl}" width="800" height="600" style="border:0" allowfullscreen></iframe>`,
  );

  function legacyCopy(text: string): boolean {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  // navigator.clipboard chỉ chạy ở secure context (HTTPS/localhost) — fallback cho HTTP/IP
  async function doCopy(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fallthrough */
    }
    return legacyCopy(text);
  }

  async function copyLink() {
    if (await doCopy(shareUrl)) onCopy(t('toast.copiedLink'));
  }
  async function copyEmbed() {
    if (await doCopy(iframeCode)) onCopy(t('toast.copiedEmbed'));
  }
</script>

<div
  class="scrim"
  role="button"
  tabindex="-1"
  aria-label={t('common.close')}
  onclick={onClose}
  onkeydown={(e) => e.key === 'Escape' && onClose()}
></div>
<div class="dialog" role="dialog" aria-modal="true" aria-label={t('share.title')}>
  <div class="head">
    <h2>{t('share.title')}</h2>
    <button class="x" title={t('common.close')} aria-label={t('common.close')} onclick={onClose}><Icon name="close" /></button>
  </div>

  <div class="qr">
    {#if qrFailed}
      <div class="qr-fallback"><Icon name="qr" size={40} /><span>{t('share.qrFallback')}</span></div>
    {:else}
      <img src={qrUrl(token, authKey)} alt="QR" onerror={() => (qrFailed = true)} />
    {/if}
  </div>

  <label class="url">
    <input type="text" readonly value={shareUrl} onfocus={(e) => e.currentTarget.select()} />
  </label>

  <button class="copy" onclick={copyLink}><Icon name="copy" size={18} /> {t('share.copyLink')}</button>

  <div class="embed">
    <span class="lbl">{t('share.embedLabel')}</span>
    <textarea
      class="embed-code"
      readonly
      rows="3"
      value={iframeCode}
      onfocus={(e) => e.currentTarget.select()}
    ></textarea>
    <button class="copy ghost" onclick={copyEmbed}>
      <Icon name="copy" size={18} /> {t('share.copyEmbed')}
    </button>
  </div>
</div>

<style>
  .scrim {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    background: var(--color-overlay-scrim);
    border: none;
  }
  .dialog {
    position: fixed;
    z-index: var(--z-modal);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(340px, calc(100vw - 2 * var(--space-lg)));
    padding: var(--space-lg);
    background: var(--color-surface);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  h2 {
    margin: 0;
    font-size: var(--font-size-title);
    color: var(--color-navy);
  }
  .x {
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 4px;
  }
  .x:hover {
    color: var(--color-error);
  }
  .qr {
    align-self: center;
    width: 200px;
    height: 200px;
    display: grid;
    place-items: center;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    overflow: hidden;
  }
  .qr img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .qr-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xs);
    color: var(--color-text-secondary);
    font-size: var(--font-size-caption);
    text-align: center;
    padding: var(--space-md);
  }
  .url input {
    width: 100%;
    padding: 10px var(--space-sm);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    font: inherit;
    color: var(--color-text-primary);
    background: var(--color-canvas-top);
  }
  .copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: var(--size-touch-min);
    border: none;
    border-radius: var(--radius-control);
    background: var(--color-primary);
    color: #fff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  .copy:hover {
    background: var(--color-primary-accent);
  }
  .embed {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
    border-top: 1px solid var(--color-border);
    padding-top: var(--space-md);
  }
  .embed .lbl {
    font-size: var(--font-size-caption);
    font-weight: 700;
    color: var(--color-text-secondary);
  }
  .embed-code {
    width: 100%;
    resize: none;
    padding: var(--space-xs) var(--space-sm);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    font-family: ui-monospace, monospace;
    font-size: var(--font-size-caption);
    color: var(--color-text-primary);
    background: var(--color-canvas-top);
  }
  .copy.ghost {
    background: var(--color-surface);
    color: var(--color-primary);
    border: 1px solid var(--color-primary);
  }
  .copy.ghost:hover {
    background: var(--color-primary);
    color: #fff;
  }
</style>
